import React from "react";
import { Link } from "@mui/material";

/**
 * Regex to detect URLs (http, https, www.)
 * Captures the full URL including optional trailing punctuation that we trim for the href
 */
const URL_REGEX = /(https?:\/\/[^\s<>"\']+|www\.[^\s<>"\']+)/gi;

/**
 * Trims trailing punctuation that is typically not part of URLs
 */
function trimTrailingPunctuation(url: string): string {
  return url.replace(/[.,;:!?)\]]+$/, "");
}

interface LinkifiedTextProps {
  children: string;
  /** Optional sx to pass to Link components */
  linkSx?: object;
}

/**
 * Renders text with URLs as clickable links that open in a new tab.
 * Use as children of Typography or inside any text container.
 *
 * @example
 * <Typography variant="body2">
 *   <LinkifiedText>{description}</LinkifiedText>
 * </Typography>
 */
const LinkifiedText: React.FC<LinkifiedTextProps> = ({ children, linkSx }) => {
  if (!children || typeof children !== "string") {
    return <>{children}</>;
  }

  const parts = children.split(URL_REGEX);

  if (parts.length === 1) {
    return <>{children}</>;
  }

  return (
    <>
      {parts.map((part, i) => {
        const isUrl =
          /^https?:\/\//i.test(part) || /^www\./i.test(part);
        if (isUrl) {
          const href = part.startsWith("www.")
            ? `https://${trimTrailingPunctuation(part)}`
            : trimTrailingPunctuation(part);
          const displayText = trimTrailingPunctuation(part);
          return (
            <Link
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                wordBreak: "break-all",
                ...linkSx,
              }}
            >
              {displayText}
            </Link>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

export default LinkifiedText;
